import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AuthService, UserInfo } from '../../../services/auth/auth.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-auth-debug',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="debug-container">
      <h2>Authentication Debug Tool</h2>
      
      <div class="section">
        <h3>Token Information</h3>
        <div class="info-block">
          <p><strong>Token Exists:</strong> {{ !!token }}</p>
          <p *ngIf="token"><strong>Token:</strong> {{ tokenPreview }}</p>
          <p><strong>Token Length:</strong> {{ token?.length || 0 }}</p>
          <div *ngIf="tokenParts.length === 3">
            <p><strong>Token Format:</strong> Valid (3 parts)</p>
            <p><strong>Header:</strong> {{ tokenParts[0].length }} chars</p>
            <p><strong>Payload:</strong> {{ tokenParts[1].length }} chars</p>
            <p><strong>Signature:</strong> {{ tokenParts[2].length }} chars</p>
          </div>
          <div *ngIf="tokenParts.length !== 3">
            <p><strong>Token Format:</strong> Invalid ({{ tokenParts.length }} parts, should be 3)</p>
          </div>
        </div>
      </div>
      
      <div class="section">
        <h3>Local Storage</h3>
        <div class="info-block">
          <p *ngFor="let item of localStorageItems">
            <strong>{{ item.key }}:</strong> {{ item.value }}
          </p>
        </div>
      </div>
      
      <div class="section">
        <h3>User Information</h3>
        <div class="info-block">
          <div *ngIf="userInfo">
            <p><strong>Username:</strong> {{ userInfo.username }}</p>
            <p><strong>Email:</strong> {{ userInfo.email }}</p>
            <p><strong>Role:</strong> {{ userInfo.role }}</p>
            <p><strong>ID:</strong> {{ userInfo.id }}</p>
            <p><strong>Company ID:</strong> {{ userInfo.company_id || 'None' }}</p>
          </div>
          <div *ngIf="!userInfo">
            <p>No user info available</p>
          </div>
        </div>
      </div>
      
      <div class="actions">
        <button (click)="testTokenRequest()">Test Token</button>
        <button (click)="testEmployeesRequest()">Test Employees Endpoint</button>
        <button (click)="testDocumentsRequest()">Test Documents Endpoint</button>
        <button (click)="testPersonalEventsRequest()">Test Personal Events</button>
        <button (click)="testNotificationSettingsRequest()">Test Notification Settings</button>
        <button (click)="fixToken()">Fix Token Format</button>
        <button (click)="clearToken()">Clear Token</button>
      </div>
      
      <div class="section" *ngIf="testResult">
        <h3>Test Result</h3>
        <div class="info-block result">
          <p><strong>Status:</strong> {{ testResult.success ? 'Success' : 'Failed' }}</p>
          <p><strong>Endpoint:</strong> {{ testResult.endpoint }}</p>
          <p *ngIf="testResult.message"><strong>Message:</strong> {{ testResult.message }}</p>
          <pre *ngIf="testResult.data">{{ testResult.data | json }}</pre>
          <pre *ngIf="testResult.error">{{ testResult.error | json }}</pre>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .debug-container {
      max-width: 800px;
      margin: 20px auto;
      padding: 20px;
      background-color: #f9f9f9;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    h2 {
      color: #333;
      margin-bottom: 20px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 10px;
    }
    
    .section {
      margin-bottom: 20px;
    }
    
    h3 {
      color: #555;
      margin-bottom: 10px;
    }
    
    .info-block {
      background-color: white;
      padding: 15px;
      border-radius: 4px;
      border: 1px solid #ddd;
    }
    
    .actions {
      display: flex;
      gap: 10px;
      margin: 20px 0;
      flex-wrap: wrap;
    }
    
    button {
      padding: 8px 16px;
      background-color: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    
    button:hover {
      background-color: #0069d9;
    }
    
    .result {
      max-height: 300px;
      overflow: auto;
    }
    
    pre {
      background-color: #f5f5f5;
      padding: 10px;
      border-radius: 4px;
      overflow: auto;
    }
  `]
})
export class AuthDebugComponent implements OnInit {
  token: string | null = null;
  tokenPreview: string = '';
  tokenParts: string[] = [];
  userInfo: UserInfo | null = null;
  localStorageItems: { key: string, value: string }[] = [];
  testResult: any = null;
  
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}
  
  ngOnInit(): void {
    this.refreshTokenInfo();
    
    // Subscribe to auth state changes
    this.authService.userInfo$.subscribe(info => {
      this.userInfo = info;
    });
    
    // Refresh local storage data
    this.refreshLocalStorageItems();
  }
  
  refreshTokenInfo(): void {
    this.token = localStorage.getItem('auth_token');
    
    if (this.token) {
      // Create preview
      this.tokenPreview = this.token.length > 40 
        ? this.token.substring(0, 20) + '...' + this.token.substring(this.token.length - 10)
        : this.token;
      
      // Split into parts
      this.tokenParts = this.token.split('.');
    } else {
      this.tokenPreview = '';
      this.tokenParts = [];
    }
  }
  
  refreshLocalStorageItems(): void {
    this.localStorageItems = [];
    
    const authRelatedKeys = [
      'auth_token', 'username', 'userRole', 'userId', 'companyId', 'email'
    ];
    
    authRelatedKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        this.localStorageItems.push({ 
          key, 
          value: key === 'auth_token' ? value.substring(0, 20) + '...' : value
        });
      }
    });
  }
  
  testTokenRequest(): void {
    this.token = localStorage.getItem('auth_token');
    
    if (!this.token) {
      this.testResult = {
        success: false,
        endpoint: '/api/auth/token-debug',
        message: 'No token available in localStorage'
      };
      return;
    }
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token.trim()}`
    });
    
    this.http.get(`${environment.apiUrl}/auth/token-debug`, { headers }).subscribe({
      next: (data) => {
        this.testResult = {
          success: true,
          endpoint: '/api/auth/token-debug',
          data: data
        };
      },
      error: (error) => {
        this.testResult = {
          success: false,
          endpoint: '/api/auth/token-debug',
          message: error.message,
          error: error
        };
      }
    });
  }
  
  testEmployeesRequest(): void {
    this.http.get(`${environment.apiUrl}/employees`).subscribe({
      next: (data) => {
        this.testResult = {
          success: true,
          endpoint: '/api/employees',
          data: data
        };
      },
      error: (error) => {
        this.testResult = {
          success: false,
          endpoint: '/api/employees',
          message: error.message,
          error: error
        };
      }
    });
  }
  
  testDocumentsRequest(): void {
    this.http.get(`${environment.apiUrl}/documents`).subscribe({
      next: (data) => {
        this.testResult = {
          success: true,
          endpoint: '/api/documents',
          data: data
        };
      },
      error: (error) => {
        this.testResult = {
          success: false,
          endpoint: '/api/documents',
          message: error.message,
          error: error
        };
      }
    });
  }
  
  testPersonalEventsRequest(): void {
    this.http.get(`${environment.apiUrl}/personal-events`).subscribe({
      next: (data) => {
        this.testResult = {
          success: true,
          endpoint: '/api/personal-events',
          data: data
        };
      },
      error: (error) => {
        this.testResult = {
          success: false,
          endpoint: '/api/personal-events',
          message: error.message,
          error: error
        };
      }
    });
  }
  
  testNotificationSettingsRequest(): void {
    this.http.get(`${environment.apiUrl}/notification-settings`).subscribe({
      next: (data) => {
        this.testResult = {
          success: true,
          endpoint: '/api/notification-settings',
          data: data
        };
      },
      error: (error) => {
        this.testResult = {
          success: false,
          endpoint: '/api/notification-settings',
          message: error.message,
          error: error
        };
      }
    });
  }
  
  fixToken(): void {
    const token = localStorage.getItem('auth_token');
    
    if (token) {
      const cleanToken = token.trim();
      if (cleanToken !== token) {
        localStorage.setItem('auth_token', cleanToken);
        this.refreshTokenInfo();
        this.refreshLocalStorageItems();
        
        this.testResult = {
          success: true,
          endpoint: 'local',
          message: 'Token whitespace cleaned'
        };
      } else {
        this.testResult = {
          success: true,
          endpoint: 'local',
          message: 'Token already clean'
        };
      }
      
      // Refresh auth state
      this.authService.refreshAuthState();
    } else {
      this.testResult = {
        success: false,
        endpoint: 'local',
        message: 'No token available to fix'
      };
    }
  }
  
  clearToken(): void {
    localStorage.removeItem('auth_token');
    this.refreshTokenInfo();
    this.refreshLocalStorageItems();
    
    // Log out through service to clean up state
    this.authService.logout();
    
    this.testResult = {
      success: true,
      endpoint: 'local',
      message: 'Token cleared'
    };
  }
} 